/**
 * HeaderCapacityMeter — the compact capacity read-out that lives in the app header.
 *
 * The panel meter (`UsageMeterBar`) is a full-width card with a label row, a value row and a
 * track. The header has 56px of height and shares it with a breadcrumb, an environment
 * indicator and three buttons, so this is a different SHAPE of the same fact: label, `used/limit`
 * and a short track on one line.
 *
 * It is not a second implementation. `getMeterStatus`, `getPercentage` and `getFillColor` are
 * imported from the panel meter, so the 80% amber / 100% over thresholds and their colours have
 * exactly one definition. Two copies of "80" would eventually disagree, and the disagreement
 * would be silent — one surface warning while the other looks healthy.
 *
 * ⚠️ **Display only.** The decided commercial model makes capacity ADVISORY: over-limit is
 * recorded and notified, not refused (DEBT-59). Nothing here gates anything, and "over" must
 * therefore read as information rather than as an error — it is amber-to-red on the bar and a
 * plain count in the text, never a blocking state.
 */

import * as React from 'react';
import { cn } from '../utils.js';
import { getMeterStatus, getPercentage, getFillColor } from './UsageMeterBar.js';

export interface HeaderCapacityDimension {
  /** Short label — the header has no room for a sentence. */
  readonly label: string;
  readonly used: number;
  /** The plan limit. `-1` (unlimited) and `0` (unknown) both render as a count with no track. */
  readonly limit: number;
  /** Billing dimension id, surfaced as a data attribute for tests + analytics. */
  readonly dimension?: string;
}

export interface HeaderCapacityMeterProps {
  readonly dimensions: readonly HeaderCapacityDimension[];
  /** Click-through to the full usage panel. */
  readonly onClick?: () => void;
  readonly className?: string;
}

/** Unlimited (-1) and unknown (0) are both "no ceiling to draw", but they are not the same word. */
function isBounded(limit: number): boolean {
  return limit > 0;
}

function OneMeter({ d }: { readonly d: HeaderCapacityDimension }): React.JSX.Element {
  const bounded = isBounded(d.limit);
  const status = getMeterStatus(d.used, d.limit);
  const pct = getPercentage(d.used, d.limit);

  return (
    <span
      className="flex items-center gap-1.5 whitespace-nowrap"
      {...(d.dimension ? { 'data-dimension': d.dimension } : {})}
      data-status={status}
    >
      <span className="text-muted-foreground">{d.label}</span>
      <span className="font-mono tabular-nums text-foreground">
        {/* An unlimited dimension shows the count alone. Rendering "12/∞" invites the reader to
            look for a proportion that does not exist. */}
        {bounded ? `${d.used}/${d.limit}` : d.used}
      </span>
      {bounded && (
        <span
          className="relative overflow-hidden w-10 shrink-0"
          style={{
            height: 'var(--meter-height)',
            background: 'var(--meter-track)',
            borderRadius: 'var(--meter-radius)',
          }}
        >
          <span
            className="absolute left-0 top-0 h-full transition-all duration-300 ease-out"
            style={{ width: `${pct}%`, background: getFillColor(status), borderRadius: 'inherit' }}
          />
        </span>
      )}
    </span>
  );
}

export function HeaderCapacityMeter({
  dimensions,
  onClick,
  className,
}: HeaderCapacityMeterProps): React.JSX.Element | null {
  if (dimensions.length === 0) return null;

  // The whole read-out gets ONE accessible label. Announcing four separate numbers as the user
  // tabs past the header is noise; "Systems 12 of 30, Relationships 78 of 100" is the fact.
  const summary = dimensions
    .map((d) => (isBounded(d.limit) ? `${d.label} ${d.used} of ${d.limit}` : `${d.label} ${d.used}`))
    .join(', ');

  const Wrapper: React.ElementType = onClick ? 'button' : 'div';
  const wrapperProps: Record<string, unknown> = onClick ? { type: 'button', onClick } : {};

  return (
    <Wrapper
      {...wrapperProps}
      aria-label={`Capacity: ${summary}`}
      className={cn(
        'hidden md:flex items-center gap-4 text-xs rounded-md px-2 py-1',
        onClick && 'hover:bg-(--content-background) transition-colors',
        onClick && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        className,
      )}
    >
      {dimensions.map((d) => <OneMeter key={d.label} d={d} />)}
    </Wrapper>
  );
}
